// FILE: src/components/ErrorBoundary.tsx
// ROLE: Global React error boundary
// PURPOSE: Show friendly crash screen + log real error message/stack
// SECTION: ERROR_BOUNDARY
// MARKER: ERROR_BOUNDARY_LOG_FIX_2026_02_08_A

import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

function normalizeError(err: unknown) {
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack };
  }
  try {
    return { name: "NonErrorThrown", message: String(err), stack: undefined as string | undefined };
  } catch {
    return { name: "NonErrorThrown", message: "unknown", stack: undefined as string | undefined };
  }
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // SECTION: ERROR_BOUNDARY_LOGGING
    // PURPOSE: Avoid "{}" by logging message/stack explicitly
    // MARKER: ERROR_BOUNDARY_LOGGING_2026_02_08_A
    const info = normalizeError(error);
    console.error("[ErrorBoundary] caught:", info);
    if (errorInfo?.componentStack) {
      console.error("[ErrorBoundary] componentStack:", errorInfo.componentStack);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          className="min-h-screen flex items-center justify-center bg-background p-4"
        >
          <div className="max-w-md w-full text-center space-y-6">
            <div
              className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center"
              aria-hidden="true"
            >
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>

            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
              <p className="text-muted-foreground">
                We're sorry, but something unexpected happened. Please try refreshing the page
                or go back to the home page.
              </p>
            </div>

            {/* Show details when running locally (Vite) */}
            {import.meta.env.DEV && this.state.error && (
              <details className="text-left bg-muted/50 rounded-lg p-4 text-sm">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground font-medium">
                  Error details (development only)
                </summary>
                <pre
                  className="mt-2 overflow-auto text-xs text-destructive whitespace-pre-wrap"
                  aria-label="Error stack trace"
                >
                  {`${this.state.error.name}: ${this.state.error.message}\n\n${this.state.error.stack ?? ""}\n\n${
                    this.state.errorInfo?.componentStack ?? ""
                  }`}
                </pre>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={this.handleRetry}
                variant="default"
                className="gap-2"
                aria-label="Try again to reload the component"
              >
                <RefreshCw className="w-4 h-4" aria-hidden="true" />
                Try Again
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="gap-2"
                aria-label="Go to home page"
              >
                <Home className="w-4 h-4" aria-hidden="true" />
                Go Home
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              If this problem persists, please contact support.
            </p>
          </div>

          <div className="sr-only" role="status" aria-live="polite">
            An error occurred. You can try again or navigate to the home page.
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
